import { fetchDocsManifest } from "@/lib/docs/load";
import { docsArticlePath } from "@/lib/docs/parse";
import type { DocsManifest } from "@/lib/docs/types";
import { ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";

function Sidebar({
	manifest,
	onNavigate,
}: {
	manifest: DocsManifest;
	onNavigate?: () => void;
}) {
	const { sectionId, slug } = useParams();
	return (
		<nav className="space-y-4" aria-label="Documentation">
			{manifest.sections.map((sec) => (
				<div key={sec.id}>
					<p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--docs-muted)]">
						{sec.title}
					</p>
					<ul className="space-y-0.5">
						{sec.pages.map((p) => {
							const to = docsArticlePath(sec.id, p.slug);
							const active = sectionId === sec.id && slug === p.slug;
							return (
								<li key={p.slug}>
									<NavLink
										to={to}
										onClick={onNavigate}
										className={cn(
											"block rounded-md px-2 py-1.5 text-[13px]",
											active
												? "bg-[var(--docs-link)]/10 font-medium text-[var(--docs-link)]"
												: "text-[var(--docs-fg)]/80 hover:bg-[var(--docs-code-bg)]",
										)}
									>
										{p.title}
									</NavLink>
								</li>
							);
						})}
					</ul>
				</div>
			))}
		</nav>
	);
}

/**
 * Academic docs shell — separate from DeFi and Agent layers.
 * Content from /docs/* (frontend-v2/public/docs → production docs assets).
 */
export function DocsLayout() {
	const [mobileOpen, setMobileOpen] = useState(false);
	const { data: manifest, isLoading, error } = useQuery({
		queryKey: ["docs-manifest"],
		queryFn: fetchDocsManifest,
		staleTime: 60_000,
	});

	return (
		<div data-docs-ui className="docs-shell min-h-screen min-h-dvh">
			<header className="sticky top-0 z-40 border-b border-[var(--docs-border)] bg-[var(--docs-bg)]/95 backdrop-blur">
				<div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4">
					{manifest ? (
						<button
							type="button"
							className="inline-flex size-10 items-center justify-center rounded-lg border border-[var(--docs-border)] lg:hidden"
							aria-label="Open documentation menu"
							onClick={() => setMobileOpen(true)}
						>
							<Menu className="size-5" />
						</button>
					) : null}

					<div className="flex min-w-0 items-center gap-2">
						<BookOpen className="size-5 shrink-0 text-[var(--docs-link)]" />
						<div className="min-w-0">
							<p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--docs-muted)]">
								MotokoDollar
							</p>
							<p className="font-serif text-base font-semibold tracking-tight text-[var(--docs-fg)]">
								Documentation
							</p>
						</div>
					</div>

					{manifest ? (
						<span className="hidden text-[11px] text-[var(--docs-muted)] sm:inline">
							v{manifest.version}
						</span>
					) : null}

					<div className="ml-auto flex flex-wrap items-center gap-2 text-xs font-medium">
						<Link
							to={ROUTES.borrow}
							className="rounded-md border border-[var(--docs-border)] px-2.5 py-1.5 text-[var(--docs-fg)] hover:border-[var(--docs-link)]"
						>
							DeFi platform
						</Link>
						<Link
							to={ROUTES.agents}
							className="rounded-md border border-[var(--docs-border)] px-2.5 py-1.5 text-[var(--docs-fg)] hover:border-[var(--docs-link)]"
						>
							Agent Layer
						</Link>
					</div>
				</div>
			</header>

			{mobileOpen && manifest ? (
				<div className="fixed inset-0 z-50 lg:hidden">
					<button
						type="button"
						className="absolute inset-0 bg-black/40"
						aria-label="Close menu"
						onClick={() => setMobileOpen(false)}
					/>
					<div className="absolute inset-y-0 left-0 w-[min(100%,20rem)] overflow-y-auto bg-[var(--docs-bg)] p-4 shadow-lift">
						<div className="mb-4 flex items-center justify-between">
							<p className="text-sm font-semibold">Documentation</p>
							<button
								type="button"
								className="rounded-lg p-2"
								onClick={() => setMobileOpen(false)}
								aria-label="Close"
							>
								<X className="size-5" />
							</button>
						</div>
						<Sidebar
							manifest={manifest}
							onNavigate={() => setMobileOpen(false)}
						/>
					</div>
				</div>
			) : null}

			<div className="mx-auto grid max-w-6xl gap-8 px-4 py-8 lg:grid-cols-[14rem_1fr]">
				<aside className="hidden lg:block">
					{isLoading ? (
						<p className="text-sm text-[var(--docs-muted)]">Loading nav…</p>
					) : error ? (
						<p className="text-sm text-red-600">Failed to load manifest</p>
					) : manifest ? (
						<Sidebar manifest={manifest} />
					) : null}
				</aside>
				<main id="docs-main" className="min-w-0">
					<Outlet context={{ manifest }} />
				</main>
			</div>
		</div>
	);
}
